/** screen_tour_activity_details.xml — a vertical list under a transparent toolbar that
 *  fades in on scroll, over a sticky bottom bar. Spec §1B, §4.1, §4.2. */
import { useRef, useState } from 'react';

import { adaptActivity } from './adapters/activity';
import { BottomBar, Toolbar } from './chrome/Chrome';
import { ItemView, type ItemContext } from './items/Items';
import { mapActivityDetails } from './mappers/activityDetails';
import { imageWidthPx } from '../../deviceProfiles';
import type { ScreenProps } from '../../types';

/** uikit_slider_image_height — also the scroll distance the toolbar fades over. */
const SLIDER_HEIGHT = 312;

export function ActivityScreen({ payload, device, imagesBase }: ScreenProps) {
  const activity = adaptActivity(payload);
  // The payload carries a currency id but no currency table, so no symbol is resolvable.
  const ui = mapActivityDetails(activity, null);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [alpha, setAlpha] = useState(0);

  const toolbarHeight = device.statusBarDp + 56;
  const fadeRange = SLIDER_HEIGHT - toolbarHeight;

  const ctx: ItemContext = { imagesBase, imageWidthPx: imageWidthPx(device) };

  return (
    <div style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      <Toolbar title={ui.title} statusBarDp={device.statusBarDp} alpha={alpha} />

      <div
        ref={scrollRef}
        onScroll={(e) => {
          const top = (e.target as HTMLDivElement).scrollTop;
          setAlpha(fadeRange > 0 ? Math.min(1, top / fadeRange) : 1);
        }}
        style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}
      >
        {ui.items.map((item) => (
          <ItemView key={item.key} item={item} ctx={ctx} />
        ))}
        {/* clear the bottom bar */}
        <div style={{ height: ui.bottomModel ? 80 + device.navBarDp : device.navBarDp }} />
      </div>

      {ui.bottomModel && (
        <BottomBar
          title={ui.bottomModel.title}
          caption={ui.bottomModel.caption}
          buttonText={ui.bottomModel.buttonText}
          buttonPlacement={ui.bottomModel.buttonPlacement}
          navBarDp={device.navBarDp}
        />
      )}
    </div>
  );
}

/** Notes the renderer wants to show outside the device frame. */
export function activityScreenNotes(payload: Record<string, unknown>): string[] {
  return mapActivityDetails(adaptActivity(payload), null).notes;
}
